/**
 * space/farScene.ts — Far scene: renders the universe camera-relative.
 *
 * M3.2 re-architecture:
 *   - 1 unit = 1 km. Body positions = (bodyKm − shipKm) directly (f64→f32 cast).
 *   - Single camera (XR-managed), near=0.05, far=1.5e7, never changes at runtime.
 *   - farRoot counter-translation: the XR camera's world position is applied inversely
 *     to a root Group so the far field is rotation-only. Standard WebXR skybox technique.
 *   - Distance clamp beyond 1e7 km preserves angular size.
 *
 * M6 NOTE: Saturn's ring shader must analytically discard fragments occluded by the
 * planet sphere (it already computes the geometry for shadows) rather than rely on
 * the depth buffer, which has ~tens-of-thousands-km error at planetary distances.
 */

import * as THREE from 'three';

// ── Far scene root ─────────────────────────────────────────────
const _farRoot = new THREE.Group();
const _farScene = new THREE.Scene();
_farScene.add(_farRoot);

// Distance clamp: beyond this many km, preserve angular size
const MAX_DIST_KM = 1e7;

// Temp (module scope, no GC)
const _rel = new THREE.Vector3();

export function getFarScene(): THREE.Scene {
  return _farScene;
}

export function getFarRoot(): THREE.Group {
  return _farRoot;
}

// ── Ship position (km, sim space) ──────────────────────────────
let _shipX = 0, _shipY = 0, _shipZ = 0;

export function setShipPosition(x: number, y: number, z: number): void {
  _shipX = x; _shipY = y; _shipZ = z;
}

export function getShipPosition(): { x: number; y: number; z: number } {
  return { x: _shipX, y: _shipY, z: _shipZ };
}

/**
 * Update a celestial body's mesh position relative to the ship.
 * bodyPosKm: heliocentric position in km.
 * bodyRadiusKm: physical radius in km (used for distance clamp angular-size preservation).
 */
export function updateBodyMesh(
  mesh: THREE.Object3D,
  bodyPosKm: { x: number; y: number; z: number },
  bodyRadiusKm: number,
): void {
  // Relative position in km (f64 subtract, then cast to float32 via Vector3)
  const rx = bodyPosKm.x - _shipX;
  const ry = bodyPosKm.y - _shipY;
  const rz = bodyPosKm.z - _shipZ;

  const dist = Math.sqrt(rx * rx + ry * ry + rz * rz);

  if (dist > MAX_DIST_KM) {
    // Distance clamp: preserve angular size
    const scale = MAX_DIST_KM / dist;
    mesh.position.set(rx * scale, ry * scale, rz * scale);
    mesh.scale.setScalar(scale * (dist / MAX_DIST_KM));
  } else {
    mesh.position.set(rx, ry, rz);
    mesh.scale.setScalar(1.0);
  }
}

/**
 * Counter-translate farRoot so the far scene is rotation-only.
 * Call every frame before rendering the far pass.
 * camWorldPos: camera world position.
 */
export function updateFarRoot(camWorldPos: THREE.Vector3): void {
  _farRoot.position.copy(camWorldPos);
}

/**
 * Compute sun light direction: normalize(sunPosKm − shipPos).
 */
export function getSunLightDir(sunPosKm: { x: number; y: number; z: number }): THREE.Vector3 {
  const dx = sunPosKm.x - _shipX;
  const dy = sunPosKm.y - _shipY;
  const dz = sunPosKm.z - _shipZ;
  const len = Math.sqrt(dx * dx + dy * dy + dz * dz);
  if (len < 1) return new THREE.Vector3(0, 0, 1);
  return new THREE.Vector3(dx / len, dy / len, dz / len);
}
